import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { parseApiError } from '@saas-kit/lib';
import { toast } from '@saas-kit/ui';

import { fetchKioskInfo, issueKioskToken, type KioskService } from './apiKiosk';
import { KioskBrandHeader } from './KioskBrandHeader';
import { KioskServiceForm } from './KioskServiceForm';
import { KioskTokenView, type KioskMode } from './KioskTokenView';
import type { QmsToken } from './types';

function KioskMessage({
  title,
  body,
}: {
  title: string;
  body?: string;
}): JSX.Element {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-2 bg-primary px-6 text-center text-primary-foreground">
      <h1 className="text-2xl font-semibold">{title}</h1>
      {body ? <p className="text-primary-foreground/70">{body}</p> : null}
    </main>
  );
}

/**
 * Public self-service kiosk. A walk-in scans the centre's printed QR, lands
 * here, picks a service, and gets a queue token — no login. Authenticated
 * solely by the signed `t` token in the query string.
 */
export function KioskPage(): JSX.Element {
  const { t } = useTranslation('qms');
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  // `mode=office` is the in-office lobby kiosk: it prints the token slip
  // instead of saving it. The default — a visitor's own phone — saves.
  const mode: KioskMode = params.get('mode') === 'office' ? 'office' : 'personal';
  // The kiosk runs as a tiny state machine: pick a service → confirm it and
  // optionally leave a name/phone → see the issued token.
  const [selected, setSelected] = useState<KioskService | null>(null);
  const [issued, setIssued] = useState<{
    token: QmsToken;
    service: KioskService;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  const query = useQuery({
    queryKey: ['qms-kiosk', token],
    queryFn: () => fetchKioskInfo(token),
    enabled: Boolean(token),
    retry: false,
  });
  const info = query.data ?? null;

  const issue = async (
    customerName: string,
    customerPhone: string,
  ): Promise<void> => {
    if (busy || !selected) return;
    setBusy(true);
    try {
      const created = await issueKioskToken(
        token,
        selected.id,
        customerName || null,
        customerPhone || null,
      );
      setIssued({ token: created, service: selected });
      setSelected(null);
    } catch (e) {
      toast.error(parseApiError(e).message);
    } finally {
      setBusy(false);
    }
  };

  const reset = (): void => {
    setIssued(null);
    setSelected(null);
  };

  if (!token || query.isError) {
    return (
      <KioskMessage
        title={t('kiosk.error_title')}
        body={t('kiosk.error_body')}
      />
    );
  }
  if (query.isLoading || !info) {
    return <KioskMessage title={t('kiosk.loading')} />;
  }
  if (issued) {
    return (
      <KioskTokenView
        info={info}
        token={issued.token}
        serviceName={issued.service.name}
        serviceNameAr={issued.service.name_ar}
        mode={mode}
        kioskToken={token}
        onDone={reset}
      />
    );
  }
  if (selected) {
    return (
      <KioskServiceForm
        info={info}
        service={selected}
        busy={busy}
        onBack={() => setSelected(null)}
        onSubmit={(name, phone) => void issue(name, phone)}
      />
    );
  }

  return (
    <main className="flex min-h-screen flex-col items-center bg-primary px-4 py-10 text-primary-foreground">
      <KioskBrandHeader info={info} />
      <p className="mt-3 text-lg text-primary-foreground/80">
        {t('kiosk.choose_service')}
      </p>
      {info.services.length === 0 ? (
        <p className="mt-10 text-primary-foreground/70">
          {t('kiosk.no_services')}
        </p>
      ) : (
        <div className="mt-8 grid w-full max-w-3xl gap-4 sm:grid-cols-2">
          {info.services.map((service) => (
            <button
              key={service.id}
              type="button"
              onClick={() => setSelected(service)}
              className="flex flex-col items-center justify-center gap-1 rounded-2xl bg-card p-6 text-xl font-semibold text-foreground shadow-lg transition hover:scale-[1.02]"
            >
              <span>{service.name}</span>
              {service.name_ar ? (
                <span dir="rtl" lang="ar" className="text-lg text-muted-foreground">
                  {service.name_ar}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      )}
    </main>
  );
}
